/* @flow */

import type { ReadOnlyKeyValuePair } from '../../../../helpers/jsHelpers/types';
import type { NETGEM_API_V8_LANG } from './Lang';
import type { NETGEM_API_V8_URL_DEFINITION } from './NtgVideoFeed';
import type { NETGEM_API_V8_PARAM } from './Param';

export enum TileConfigTileType {
  Channel = 'channel',
  ChannelGroup = 'channelgroup',
  ChannelGroupBig = 'channelgroup.big',
  ChannelGroupBigFill = 'channelgroup.big.fill',
  ChannelGroupFill = 'channelgroup.fill',
  Deeplink = 'deeplink',
  Deeplink3x1 = 'deeplink.3x1',
  Deeplink5x1 = 'deeplink.5x1',
  Gemtv = 'gemtv',
  GemtvBig = 'gemtv.big',
  GemtvMedium = 'gemtv.medium',
  Landscape = 'landscape',
  LandscapeBig = 'landscape.big',
  LandscapeVod = 'landscape.vod',
  Portrait = 'portrait',
}

export enum TileConfig {
  ProgramTitle = 'programTitle',
  SeriesTitle = 'seriesTitle',
  ProgramSynopsis = 'programSynopsis',
  SeriesSynopsis = 'seriesSynopsis',
  Date = 'date',
  Duration = 'duration',
  Year = 'year',
  Genre = 'genre',
  Language = 'language',
  ParentalGuidance = 'parentalGuidance',
}

export type TILE_CONFIG_DESCRIPTION = {
  program?: Array<TileConfig>,
  series?: Array<TileConfig>,
};

export enum TileContentImageKind {
  Channel = 'channel',
  Other = 'other',
  Program = 'program',
  Series = 'series',
}

export enum TileImageLayout {
  Stretched = 'stretched',
  Cover = 'cover',
  Contain = 'contain',
}

export enum TileOnFocus {
  Details = 'details',
  Selection = 'selection',
}

type NETGEM_API_V8_WIDGET_TILE_CONFIG_BASE_TYPE = {
  caption: ?TILE_CONFIG_DESCRIPTION,
  channelLogo: boolean,
  contentImage: TileContentImageKind,
  contentImageVariant?: string,
  hoverContent: ?TILE_CONFIG_DESCRIPTION,
  imageLayout: TileImageLayout,
  onFocus: TileOnFocus,
};

export type NETGEM_API_V8_WIDGET_TILE_CONFIG_TYPE = {
  ...NETGEM_API_V8_WIDGET_TILE_CONFIG_BASE_TYPE,
  type?: TileConfigTileType,
};

export type TILE_CONFIG_TYPE = {
  ...NETGEM_API_V8_WIDGET_TILE_CONFIG_BASE_TYPE,
  type: TileConfigTileType,
};

export enum WidgetLayoutType {
  Grid = 'grid',
  List = 'list',
}

/*
 * Available actions:
 *  card1:  open associated card
 *  hub1:   open associated hub
 *  none:   no action is performed
 *  player: open player and play the stream associated with the item
 *  switch: action depends on item
 */
export const NetgemApiV8WidgetItemClickActionKind: ReadOnlyKeyValuePair<string> = Object.freeze({
  Card: 'card1',
  Hub: 'hub1',
  None: 'none',
  Player: 'player',
  Switch: 'switch',
});

type NetgemApiV8WidgetItemClickAction = 'card1' | 'hub1' | 'none' | 'player' | 'switch';

export type NetgemApiV8WidgetItemClickParams = Array<NetgemApiV8WidgetItemClickParam>;

export type NetgemApiV8WidgetItemClickParam = {
  action: { action: 'none' } | { action: 'card1' | 'hub1', params: NETGEM_API_V8_URL_DEFINITION } | { action: 'switch', params: NetgemApiV8WidgetItemClickParams },
  prefix?: string,
};

export type NetgemApiV8WidgetItemClick = {
  action: NetgemApiV8WidgetItemClickAction,
  params?: NetgemApiV8WidgetItemClickParams | NETGEM_API_V8_URL_DEFINITION,
};

export enum GroupItemsBy {
  Program = 'program',
  Season = 'season',
  Series = 'series',
}

export type NETGEM_API_V8_WIDGET_PANEL_IMAGE_URL_TYPE = {
  params?: Array<NETGEM_API_V8_PARAM>,
  uri: string,
};

type NETGEM_API_V8_WIDGET_PANEL_TYPE = {
  kind: 'customLockPanel',
  widgetConfig: {
    title?: Array<NETGEM_API_V8_LANG>,
    info?: Array<NETGEM_API_V8_LANG>,
    imageUri?: string,
    imageUrl?: NETGEM_API_V8_WIDGET_PANEL_IMAGE_URL_TYPE,
    instructions?: Array<NETGEM_API_V8_LANG>,
  },
};

export type NETGEM_API_V8_WIDGET_CONFIG_TYPE = {
  groupItemsBy?: GroupItemsBy,
  headerUri?: string,
  imageUri?: string,
  layout?: WidgetLayoutType,
  tileConfig?: NETGEM_API_V8_WIDGET_TILE_CONFIG_TYPE,
  tileType?: TileConfigTileType,
  onEmptyAvenue?: NETGEM_API_V8_WIDGET_PANEL_TYPE,
  onItemClick?: NetgemApiV8WidgetItemClick,
};

export type NETGEM_API_V8_WIDGET_ACTIONS_TYPE = {
  onItemClick: NetgemApiV8WidgetItemClick,
};

export type NETGEM_API_V8_DEFAULT_ACTIONS = {
  actions: NETGEM_API_V8_WIDGET_ACTIONS_TYPE,
};
